import type { PaymentPayload } from '@ctct/infrastructure-layer';
import { PaymentStrategy, type PayForPlanMutation } from '@ctct/state-server-layer';

import type { Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import type {
  AlertManager,
  PaymentButtonManager,
  NonProfitManager,
  CurrencyManager,
  PrepaySelectManager,
  ContactsTierSelectManager,
  RouterNonUiManager,
  BillingInfoFormManager,
  PaymentMethodsManager,
  OrderSummaryManager,
  EmailPlansManager,
  PayflowProgressBarManager,
  PaymentProcessingInfoManager,
  AddOnsManager,
} from '../managers';
import { paymentButtonEventBus, paymentMethodsEventBus } from '../managers';
import type { AddOnsSubscriptionManager } from '../managers/AddOnsSubscriptionManager';

// to prevent even a tiny posibility of sending the request twice
let isPaying = false;

export class PaymentButtonController {
  public listeners: Listeners;

  constructor(
    private paymentMethodsManager: PaymentMethodsManager,
    private billingInfoFormManager: BillingInfoFormManager,
    private routerManager: RouterNonUiManager,
    private paymentButtonManager: PaymentButtonManager,
    private nonProfitManager: NonProfitManager,
    private currencyManager: CurrencyManager,
    private prepaySelectManager: PrepaySelectManager,
    private contactsTierSelectManager: ContactsTierSelectManager,
    private orderSummaryManager: OrderSummaryManager,
    private alertManager: AlertManager,
    private emailPlansManager: EmailPlansManager,
    private paymentProcessingInfoManager?: PaymentProcessingInfoManager,
    private payflowProgressBarManager?: PayflowProgressBarManager,
    private addOnsSubscriptionManager?: AddOnsSubscriptionManager,
    private addOnsManager?: AddOnsManager
  ) {
    this.listeners = controllerHelpers.createListeners([
      paymentButtonEventBus.bindTo('payForPlanClicked', this.onPayForPlanClicked),
      paymentButtonEventBus.bindTo('showEmailPricingTiersClick', this.onEmailPricingTiersClicked),
      paymentMethodsEventBus.bindTo('stripePostRedirectFailure', this.onStripePostRedirectFailure),
    ]);
  }

  private onPayForPlanClicked = async (payForPlanApiCall: PayForPlanMutation['mutateAsync']) => {
    const {
      alertManagerApi,
      paymentButtonApi,
      paymentMethodsApi,
      billingInfoFormApi,
      currencyApi,
      orderSummaryApi,
      routerApi,
      paymentProcessingInfoApi,
    } = await this.getApis();

    if (isPaying) return;

    isPaying = true;

    const rating = orderSummaryApi.getRating();
    const stripeQuoteId = rating?.getStripeQuoteId();
    const idempotencyKey = paymentButtonApi.getIdempotencyKey();

    try {
      await this.disableAll();
      alertManagerApi.removeAllAlerts();
      paymentProcessingInfoApi?.show();

      const billingInfoPayload = await billingInfoFormApi.getValuesForCheckoutApiRequest(currencyApi.getCurrency());

      if (paymentMethodsApi.paymentStrategy === PaymentStrategy.Stripe) {
        const canContinueCheckout = await paymentMethodsApi.handleExternalPaymentSubmission();

        if (!canContinueCheckout) {
          paymentMethodsApi.beginningExternalRedirectToCompletePayment();

          return;
        }
      }

      const paymentsPayload = await paymentMethodsApi.getPayloadForCheckoutApi({
        postalCode: billingInfoPayload.zipcode,
        countryCode: billingInfoPayload.country,
        fullName: `${billingInfoPayload.firstname} ${billingInfoPayload.lastname}`,
        email: billingInfoPayload.email,
        phone: billingInfoPayload.phone,
      });
      const ratingData = orderSummaryApi.getRatingResponse();

      if (!ratingData) {
        throw new Error('Payment method not loaded, please try again');
      }

      // filter out invalid promo codes if any
      const filteredPromotionData = ratingData.promotionData
        ? (Object.fromEntries(
            Object.entries(ratingData.promotionData).filter(([, value]) => value.planId !== null)
          ) as PaymentPayload['ratingData']['promotionData'])
        : null;

      await payForPlanApiCall({
        payment: paymentsPayload,
        customer: billingInfoPayload,
        ratingData: {
          ...ratingData,
          promotionData: filteredPromotionData,
          name: 'paymentPlan',
        },
        idempotencyKey,
        ...(stripeQuoteId ? { stripeQuoteId } : null),
      });

      paymentButtonApi.clearIdempotencyKey();

      if (paymentMethodsApi.paymentStrategy === PaymentStrategy.Stripe) {
        paymentMethodsApi.clearPaypalAgreementId();
      }

      // move to appropriate thank you page
      if (this.payflowProgressBarManager) {
        // The progress bar is only used on the payflow. There's probably a better way to do this, should revisit.
        routerApi.goToPayflowThankYou(billingInfoPayload.firstname);
      } else {
        routerApi.goToBuyNowThankYou(billingInfoPayload.firstname);
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        const errorMessage = await controllerHelpers.handleStripePaymentError(error, paymentMethodsApi);

        alertManagerApi.addAlert({
          message: errorMessage,
          id: errorMessage,
        });
      }
    } finally {
      await this.enableAll();
      paymentProcessingInfoApi?.hide();
      isPaying = false;
    }
  };

  private onStripePostRedirectFailure = async () => {
    await this.enableAll();

    const { paymentProcessingInfoApi } = await this.getApis();

    paymentProcessingInfoApi?.hide();
    isPaying = false;
  };

  private onEmailPricingTiersClicked = async () => {
    const api = await this.emailPlansManager.getApiWhenReady();

    api.showEmailPricingTierDialog();
  };

  private async disableAll() {
    const {
      paymentButtonApi,
      paymentMethodsApi,
      billingInfoFormApi,
      nonProfitApi,
      currencyApi,
      prepaySelectApi,
      contactsTierSelectApi,
      orderSummaryApi,
    } = await this.getApis();

    paymentButtonApi.disable();
    orderSummaryApi.disable();
    nonProfitApi.disable();
    currencyApi.disableSelect();
    prepaySelectApi.disable();
    contactsTierSelectApi.disable();
    paymentMethodsApi.setFullLoading(true);
    billingInfoFormApi.setIsLoading(true);
  }

  private async enableAll() {
    const {
      paymentButtonApi,
      paymentMethodsApi,
      billingInfoFormApi,
      nonProfitApi,
      currencyApi,
      prepaySelectApi,
      contactsTierSelectApi,
      orderSummaryApi,
    } = await this.getApis();

    paymentButtonApi.enable();
    orderSummaryApi.enable();
    nonProfitApi.enable();
    currencyApi.enableSelect();
    prepaySelectApi.enable();
    contactsTierSelectApi.enable();
    paymentMethodsApi.setFullLoading(false);
    billingInfoFormApi.setIsLoading(false);
  }

  private async getApis() {
    const [
      alertManagerApi,
      paymentButtonApi,
      paymentMethodsApi,
      billingInfoFormApi,
      nonProfitApi,
      currencyApi,
      prepaySelectApi,
      contactsTierSelectApi,
      orderSummaryApi,
      routerApi,
      addOnsSubscriptionApi,
      paymentProcessingInfoApi,
      addOnsApi,
    ] = await Promise.all([
      this.alertManager.getApiWhenReady(),
      this.paymentButtonManager.getApiWhenReady(),
      this.paymentMethodsManager.getApiWhenReady(),
      this.billingInfoFormManager.getApiWhenReady(),
      this.nonProfitManager.getApiWhenReady(),
      this.currencyManager.getApiWhenReady(),
      this.prepaySelectManager.getApiWhenReady(),
      this.contactsTierSelectManager.getApiWhenReady(),
      this.orderSummaryManager.getApiWhenReady(),
      this.routerManager.getApiWhenReady(),
      this.addOnsSubscriptionManager?.getApiWhenReady() ?? Promise.resolve(undefined),
      this.paymentProcessingInfoManager?.getApiWhenReady() ?? Promise.resolve(undefined),
      this.addOnsManager?.getApiWhenReady() ?? Promise.resolve(undefined),
    ]);

    return {
      alertManagerApi,
      paymentButtonApi,
      paymentMethodsApi,
      billingInfoFormApi,
      nonProfitApi,
      currencyApi,
      prepaySelectApi,
      contactsTierSelectApi,
      orderSummaryApi,
      routerApi,
      addOnsSubscriptionApi,
      paymentProcessingInfoApi,
      addOnsApi,
    };
  }
}
